import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { apiService } from '../services/api';
import type { TenantCustomField, TenantCustomFieldInput } from '../types';
import toast from 'react-hot-toast';

/** Gera a chave interna a partir do nome: minúsculas, espaços → _, sem acentos. Usada em {{custom.chave}}. */
function deriveKey(label: string): string {
  const s = label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
  if (!s) return '';
  if (/^[0-9]/.test(s)) return `field_${s}`;
  return s;
}

export function CustomFieldFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'novo';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [type, setType] = useState<'text' | 'number' | 'date'>('text');
  const [required, setRequired] = useState(false);

  useEffect(() => {
    if (!isNew && id) {
      setLoading(true);
      apiService.getCustomFields().then((list) => {
        const f = list.find((x) => x.id === id);
        if (f) {
          setKey(f.key);
          setLabel(f.label);
          setType(f.type);
          setRequired(f.required);
        }
      }).finally(() => setLoading(false));
    }
  }, [isNew, id]);

  useEffect(() => {
    if (isNew && label) {
      setKey(deriveKey(label));
    }
  }, [isNew, label]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const k = isNew ? deriveKey(label) : key.trim().toLowerCase();
    if (!label.trim()) {
      toast.error('Nome do campo é obrigatório');
      return;
    }
    if (!k || k.length < 2) {
      toast.error('O nome do campo precisa ter pelo menos 2 caracteres (a chave será gerada a partir dele).');
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(k)) {
      toast.error('O nome gera uma chave inválida. Use apenas letras, números e espaços (ex: Loja, Data de aniversário).');
      return;
    }
    setSaving(true);
    try {
      const data: TenantCustomFieldInput = { key: k, label: label.trim(), type, required };
      if (isNew) {
        await apiService.createCustomField(data);
        toast.success('Campo criado');
      } else if (id) {
        await apiService.updateCustomField(id, data);
        toast.success('Campo atualizado');
      }
      navigate('/contatos/campos-personalizados');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!isNew && loading) {
    return (
      <>
        <Header title="Campos personalizados" subtitle="Carregando..." compactTopbar />
        <PageLayout>
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Header title="Campos personalizados" subtitle={isNew ? 'Novo campo' : 'Editar campo'} compactTopbar />
      <PageLayout>
        <PageHeader
          title={isNew ? 'Novo campo' : 'Editar campo'}
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Contatos', href: '/contatos' },
            { label: 'Campos personalizados', href: '/contatos/campos-personalizados' },
            { label: isNew ? 'Novo' : 'Editar' },
          ]}
        />
        <div className="space-y-8 w-full">
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">
                {isNew ? 'Novo campo personalizado' : 'Editar campo'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="label" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Nome do campo (exibido) *
                  </label>
                  <input
                    id="label"
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Ex: Loja, Plano, Data de aniversário"
                    className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="type" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Tipo
                  </label>
                  <select
                    id="type"
                    value={type}
                    onChange={(e) => setType(e.target.value as 'text' | 'number' | 'date')}
                    className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="text">Texto</option>
                    <option value="number">Número</option>
                    <option value="date">Data</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="key" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Chave (variável)
                </label>
                <input
                  id="key"
                  type="text"
                  value={key}
                  readOnly
                  className="input-field w-full font-mono text-sm border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 cursor-default"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isNew
                    ? 'Gerada automaticamente do nome (minúsculas, espaços → _). Será usada nas mensagens como {{custom.' + (key || 'chave') + '}}.'
                    : 'Usada nas mensagens como {{custom.' + key + '}}. Não pode ser alterada.'}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="required"
                  type="checkbox"
                  checked={required}
                  onChange={(e) => setRequired(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-800"
                />
                <label htmlFor="required" className="text-sm text-gray-700 dark:text-gray-300">
                  Obrigatório ao cadastrar/editar contato
                </label>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/contatos/campos-personalizados')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                >
                  <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin text-base shrink-0" aria-hidden />
                      Salvando...
                    </>
                  ) : isNew ? (
                    <>
                      <i className="fas fa-plus text-base shrink-0" aria-hidden />
                      Criar campo
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check text-base shrink-0" aria-hidden />
                      Salvar alterações
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </PageLayout>
    </>
  );
}
